import React, {
  useState,
  useEffect,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  Autocomplete,
  Button,
  CheckCircleFillIcon,
  FormGroup,
  FormHelperText,
  HStack,
  Link,
  Spacer,
  Text,
  Tooltip,
  VStack,
  WarningCircleIcon,
  AutocompleteInputFieldSelector,
  AutocompleteInputClearButtonSelector,
  AutocompleteInputDropdownButtonSelector,
} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';
import {EmailNotificationFieldLabel} from './emailNotificationFieldLabel';
import {emailValidator} from '@packages/shared/utils/Emails';
import {isEmpty} from 'lodash';
import {UsersMapType} from '@packages/systems/designer/user';
import {WORKSPACE_ROLES} from '@packages/domains/authorization/permissions/catalog';
import {useFormEmailDestinationLimit} from './hooks/useFormEmailDestinationLimit';
import {useEmailDestinationRestriction} from './hooks/useEmailDestinationRestriction';
import {useRecipientVerificationStatus} from './hooks/useRecipientVerificationStatus';

/**
 * Help Center article outlining best practices for reliable delivery of form
 * notification emails (e.g. avoiding role-based addresses like info@, sales@,
 * support@). Surfaced below the destination field to reduce support-ticket
 * volume — see https://webflow.atlassian.net/browse/AEO-884.
 */
const FORM_NOTIFICATION_EMAIL_HELP_ARTICLE_URL =
  'https://help.webflow.com/hc/en-us/articles/40586232381331-Why-am-I-not-receiving-form-notification-emails';

export type EmailOption = {
  label: string;
  value: string;
  meta?: {tags?: string[]};
};

type EmailDestinationFieldProps = {
  emailDestinations: string[];
  setEmailDestinations: (destinations: string[]) => void;
  addEmailDestination: (destination: string) => void;
  siteUsers: UsersMapType;
  // Resends the confirmation email to one unconfirmed recipient. The action is
  // hidden when omitted.
  onResendConfirmation?: (email: string) => void;
  // The recipient whose resend is in flight, if any.
  resendingEmail?: string;
};

const RecipientChipInlineView = emotionStyled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '$1',
});

/**
 * A selected recipient chip's contents with its verification status: a green
 * check when confirmed (or a workspace member), a red warning icon + red label
 * while pending. Hovering shows the status in a Tooltip.
 */
const RecipientStatusChip = ({
  label,
  isVerified,
}: {
  label: string;
  isVerified: boolean;
}) => (
  <Tooltip
    label={
      isVerified
        ? 'Email confirmed'
        : "Not confirmed. This address won't receive form notifications until the recipient confirms it."
    }
    colorVariant={isVerified ? undefined : 'danger'}
    placement="top"
  >
    <RecipientChipInlineView>
      {isVerified ? (
        <CheckCircleFillIcon
          colorVariant="success"
          aria-label="Email confirmed"
        />
      ) : (
        <WarningCircleIcon
          colorVariant="danger"
          aria-label="Email not confirmed"
        />
      )}
      <Text colorVariant={isVerified ? undefined : 'danger'}>{label}</Text>
    </RecipientChipInlineView>
  </Tooltip>
);

export const EmailDestinationField = ({
  emailDestinations,
  setEmailDestinations,
  addEmailDestination,
  siteUsers,
  onResendConfirmation,
  resendingEmail,
}: EmailDestinationFieldProps) => {
  const toOptions = React.useMemo(
    () => getEmailOptions(siteUsers, emailDestinations || []),
    [siteUsers, emailDestinations]
  );
  const [emailDestinationError, setEmailDestinationError] = useState(false);
  const [emailDestinationErrorMessage, setEmailDestinationErrorMessage] =
    useState('');

  // Check if email destinations are restricted to workspace members
  const {isRestricted, allowedEmailDestinations} =
    useEmailDestinationRestriction();

  // Verification status per recipient (for the "verified" checkmark). Only
  // meaningful when the site enforces recipient email verification.
  const {enforced: isVerificationEnforced, verifiedEmails} =
    useRecipientVerificationStatus(emailDestinations);

  const unconfirmedEmails = isVerificationEnforced
    ? emailDestinations.filter(
        (email) => !verifiedEmails.has(email.trim().toLowerCase())
      )
    : [];

  const emailDestinationLimit = useFormEmailDestinationLimit();
  // Check if the number of email destinations exceeds the limit
  const hasEmailDestinationCountError =
    emailDestinationLimit != null &&
    emailDestinations.length > emailDestinationLimit;

  const emailDestinationCountErrorMessage = hasEmailDestinationCountError
    ? `Free sites are limited to ${emailDestinationLimit} email destination${emailDestinationLimit === 1 ? '' : 's'}. Please upgrade your site plan to add more email destinations.`
    : '';

  const hasError =
    (
      setErrorState: Dispatch<SetStateAction<boolean>>,
      setErrorMessage: Dispatch<SetStateAction<string>>
    ) =>
    (email: string) => {
      const errors = emailValidator({email: email.trim()});
      // Right now there's no required field
      if (email && !isEmpty(errors)) {
        setErrorState(true);
        setErrorMessage(`${email.trim()} is not a valid email address`);
        return true;
      }
      return false;
    };

  useEffect(() => {
    setEmailDestinationError(false);
    emailDestinations.forEach(
      hasError(setEmailDestinationError, setEmailDestinationErrorMessage)
    );
  }, [emailDestinations]);

  const handleEmailDestinationBlur = () => {
    setEmailDestinationError(false);
    setEmailDestinationErrorMessage('');
    emailDestinations.forEach(
      hasError(setEmailDestinationError, setEmailDestinationErrorMessage)
    );
  };

  // When the user adds a custom email destination
  const handleCreateOption = (email: string) => {
    if (!email.trim()) return;

    // check email validation
    if (
      hasError(setEmailDestinationError, setEmailDestinationErrorMessage)(email)
    ) {
      return;
    }

    // check if value is redundant
    if (emailDestinations.includes(email)) {
      setEmailDestinationError(true);
      setEmailDestinationErrorMessage('Email already exists');
      return;
    } else if (toOptions.find((option) => option.value === email)) {
      addEmailDestination(email);
      return;
    }

    if (
      isRestricted &&
      allowedEmailDestinations &&
      !allowedEmailDestinations.has(email)
    ) {
      setEmailDestinationError(true);
      setEmailDestinationErrorMessage(
        'Free sites can only add email destinations from workspace members. Please upgrade your site plan to add external email destinations.'
      );
      return;
    }

    // updating the emailDestinations will re-calculate the toOptions
    addEmailDestination(email);
  };

  const renderOptionLabel = (
    option: EmailOption,
    {context}: {context: string}
  ) => {
    if (context === 'menu') {
      // When the user has no name, show just the email address without extra spacing.
      const displayLabel = option.label
        ? `${option.label} <${option.value}>`
        : option.value;
      return <Text>{displayLabel}</Text>;
    }
    // Fall back to email address when the label is empty (e.g. workspace
    // members who have no first/last name set), so the selected chip always
    // displays a meaningful string instead of an invisible blank tag.
    const label = option.label || option.value;

    // On sites that enforce recipient verification, show each recipient's status:
    // a green check once they've confirmed their email, or a red warning icon and
    // red label while pending. Unverified recipients don't receive notifications
    // until confirmed.
    if (isVerificationEnforced) {
      const isVerified = verifiedEmails.has(option.value.toLowerCase());
      return <RecipientStatusChip label={label} isVerified={isVerified} />;
    }

    return <Text>{label}</Text>;
  };

  return (
    <FormGroup
      isError={emailDestinationError || hasEmailDestinationCountError}
      id="email-notification-to"
    >
      <EmailNotificationFieldLabel
        htmlFor="email-notification-to"
        text="To"
        helpText="Enter the email addresses of the people who will receive the email notifications."
      />
      <Tooltip
        label={
          emailDestinationErrorMessage || emailDestinationCountErrorMessage
        }
        isOpen={emailDestinationError || hasEmailDestinationCountError}
        // An enabled Tooltip disables every Tooltip nested inside it, so turn
        // this one off when there's no error, or the recipient chips' status
        // tooltips never open.
        isDisabled={!(emailDestinationError || hasEmailDestinationCountError)}
        colorVariant="danger"
        placement="top"
      >
        <AutocompleteView
          placeholder="Enter email address"
          selectionMode="multiple"
          isCreatable
          isError={hasEmailDestinationCountError}
          options={toOptions}
          onCreateOption={handleCreateOption}
          renderOptionLabel={renderOptionLabel}
          onChange={setEmailDestinations}
          onBlur={handleEmailDestinationBlur}
          value={emailDestinations}
        />
      </Tooltip>
      {onResendConfirmation && unconfirmedEmails.length > 0 && (
        <VStack spacing={1}>
          {unconfirmedEmails.map((email) => (
            <HStack key={email}>
              <Text colorVariant="secondary" truncateToLines={1}>
                {email}
              </Text>
              <Spacer />
              <Button
                variant="outline"
                aria-label={`Resend confirmation email to ${email}`}
                isDisabled={resendingEmail === email}
                onClick={() => onResendConfirmation(email)}
              >
                Resend
              </Button>
            </HStack>
          ))}
        </VStack>
      )}
      <FormHelperText>
        <Link
          href={FORM_NOTIFICATION_EMAIL_HELP_ARTICLE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          See best practices for reliable form notification email delivery
        </Link>
      </FormHelperText>
    </FormGroup>
  );
};

const AutocompleteView = emotionStyled(Autocomplete)({
  [AutocompleteInputFieldSelector]: {
    padding: '$2',
  },

  [AutocompleteInputClearButtonSelector]: {
    padding: '$2',
  },

  [AutocompleteInputDropdownButtonSelector]: {
    padding: '$2',
  },
});

/**
 * This function starts with the list of site users and current email destinations for the form.
 * It merges the lists into a single list of formatted options ordered by notified users, available users, and custom emails.
 *
 * @param siteUsers
 * @param emailDestinations
 * @returns EmailOption[]
 */
const getEmailOptions = (
  siteUsers: UsersMapType,
  emailDestinations: string[]
): EmailOption[] => {
  const usersAsArray = siteUsers.valueSeq();
  // First we'll assume all email destinations are not site user emails
  const nonUserEmails = [...emailDestinations];

  const notifiedUsers: EmailOption[] = [];
  const availableUsers: EmailOption[] = [];

  // Match email destinations to user and split into notified and available users
  usersAsArray.forEach((user) => {
    // Public (share-link) commenters hold a workspace role but are not real
    // workspace members — exclude them so their emails aren't offered as form
    // notification recipients across the workspace's non-SSA sites.
    if (user.roles?.workspace === WORKSPACE_ROLES.public) {
      return;
    }
    const {email, firstName, lastName} = user;
    // Include the email in `meta.tags` so the Autocomplete's default filter
    // (which matches against `label` + `meta.tags`) keeps the user option
    // visible when the user types or pastes the email address.
    const option: EmailOption = {
      label: [firstName, lastName].filter(Boolean).join(' ') || '',
      value: email || '',
      ...(email && {meta: {tags: [email]}}),
    };

    if (email) {
      if (nonUserEmails.includes(email)) {
        notifiedUsers.push(option);
        // Remove it from the array, so at the end we have only non-user emails
        nonUserEmails.splice(nonUserEmails.indexOf(email), 1);
      } else {
        availableUsers.push(option);
      }
    }
  });

  return [
    ...notifiedUsers,
    ...availableUsers,
    ...nonUserEmails.map((email) => ({label: email, value: email})),
  ];
};
